import type {DispatchBoardScenario} from '../dispatch/dispatch-board-scenario'
import type {UnconfirmedRental} from '../rentals/confirm-out-panel'
import type {PartialPickup} from '../work/partial-pickup-schema'

export type ViewRange = 'day' | '2days' | 'week' | 'month'

export type DispatchResource = {
    id: string
    name: string
    initials: string
    rentalAssetTypeId?: string
    rentalAssetTypeName?: string
    /** Latest known yard or job-site label for uniquely identified fleet. */
    locationLabel?: string
    /** When false, instances are interchangeable — assign by type and quantity. */
    isUniquelyIdentified?: boolean
    workerTypeId?: string
    homeYardId?: string | null
    /** Placeholder resource without a worker record. */
    isPlaceholder?: boolean
    unavailable?: Array<{from: string; until: string; label: string; detail: string}>
    /** When set, resource must not appear as an assign candidate. */
    outOfService?: {
        reason: string
        expectedBack?: string | null
    }
    /** Active rental on another project, starting `since`. */
    onRent?: {projectId: string; order: string; since: string}
}

/** One column on the timeline (typically one hour of the work day). */
export type DispatchTimeSlot = {
    key: string
    label: string
    /** When true, draws the current-time indicator on this slot. */
    isNow?: boolean
    /** Which day in the visible range this column belongs to (0 = anchor day). */
    dayOffset?: number
    /** First hour column of a day — used to emphasize day boundaries in multi-day views. */
    isDayStart?: boolean
}

/** Position of a job on the hourly grid for the visible schedule day. */
export type JobTimeSpan = {
    /** Day offset from the anchor date within the loaded schedule (0 = today). */
    dayOffset?: number
    /** Inclusive hour-slot index within the work day (0 = first column of that day). */
    startSlotIndex: number
    /** Inclusive hour-slot index within the work day. */
    endSlotIndex: number
    /** Job began before the visible window (bar starts at first column). */
    continuesFromPriorDay?: boolean
    /** Job runs past the visible window (bar ends at last column). */
    continuesIntoNextDay?: boolean
    /** Inclusive calendar-day span from start day (1 = single day). */
    daySpan?: number
    /** No end date from the API — busy from start onward on availability strips. */
    openEnded?: boolean
    /** Actual start time (`HH:mm`); the bar is clamped to the day columns. */
    startClock?: string
}

export type JobScheduleKind = 'one_day' | 'two_day' | 'timed'

export type BoardLayoutMode = 'rail' | 'unified' | 'commitment'

/** Operational filter for the commitment board — same jobs, different lens. */
export type AttentionLens = 'all' | 'needs_attention'

export type RequirementKind = 'worker' | 'rental_asset'

export type JobRequirement = {
    id: string
    kind: RequirementKind
    label: string
    quantity: number
    workerTypeId?: string
    isDriver?: boolean
    rentalAssetTypeId?: string
    /** Backend-computed fulfillment for stock equipment requirements. */
    assignedQuantity?: number
    onSiteQuantity?: number
}

export type JobAllocation = {
    requirementId: string
    resourceId: string
    resourceName: string
    kind: RequirementKind
    /** Stable backend assignment ID for worker notification actions. */
    assignmentId?: string
    /** POC timestamp — set when the dispatcher marks this driver notified. */
    notifiedAt?: number
    days?: BookingDays
    /** Placeholder allocation, excluded from notifications and runs. */
    isPlaceholder?: boolean
}

export type BookingDays = {from: string; until: string | null}

export function bookingCoversDay(days: BookingDays | undefined, day: string): boolean {
    return days == null || (days.from <= day && (days.until == null || days.until >= day))
}

/** Schedule fields used for dispatch eligibility; assignment strips remain day-based. */
export type DispatchJobSchedule = {
    startDate: string
    startTime: string | null
    endDate: string | null
    endTime: string | null
    dateTbc?: boolean
}

export type AllocationStatus = 'needs_resources' | 'partially_allocated' | 'ready'

/** Board job identity keyed by persisted job id — sourced once from the board payload. */
export type BoardJobIdentity = {
    description: string | null
    serviceTypeName: string
    projectId: string
    projectName: string
    repName?: string | null
    repId?: string | null
    projectCreatedById?: string | null
    createdById?: string | null
    customerId: string
    customerName: string
    dispatchYardId: string | null
    dispatchYardName: string | null
    closerYardName?: string | null
    closerYardReason?: string | null
    outOfStateLabel?: string | null
    bookByDay?: boolean
    isPickup?: boolean
    partialPickup?: PartialPickup | null
    didNotHappenAt?: string | null
    pickupConfirmedAt?: string | null
    unconfirmedRentals?: UnconfirmedRental[]
    dateTbc?: boolean
    dateTbcDayCount?: number | null
    driverOwnedTruckTypes?: string[]
    onSiteEquipmentTypes?: string[]
    displayId: string
    siteName: string | null
    startDate: string
    startTime: string | null
    endDate: string | null
    endTime: string | null
}

/** Job on the board that still needs attention or allocation. */
export type UnassignedJob = {
    id: string
    jobCode: string
    label: string
    customer: string
    customerId?: string
    dispatchYardId?: string | null
    closerYardName?: string | null
    outOfStateLabel?: string | null
    internalContactName?: string | null
    partialPickup?: PartialPickup | null
    /** Rentals put on rent automatically that dispatch has not confirmed went out. */
    unconfirmedRentalCount?: number
    span: JobTimeSpan
    scheduleKind: JobScheduleKind
    /** Raw job schedule used for time-aware resource conflict checks. */
    schedule?: DispatchJobSchedule
    warnings?: string[]
    requirements: JobRequirement[]
    allocations: JobAllocation[]
    /** When set, board load uses backend-computed fulfillment instead of inferring from allocations. */
    allocationStatus?: AllocationStatus
    boardScenario?: DispatchBoardScenario
}

export type DispatchAssignment = {
    id: string
    resourceId: string
    jobCode: string
    label: string
    customer: string
    customerId?: string
    dispatchYardId?: string | null
    closerYardName?: string | null
    outOfStateLabel?: string | null
    internalContactName?: string | null
    partialPickup?: PartialPickup | null
    /** Rentals put on rent automatically that dispatch has not confirmed went out. */
    unconfirmedRentalCount?: number
    span: JobTimeSpan
    scheduleKind: JobScheduleKind
    /** Raw job schedule used for time-aware resource conflict checks. */
    schedule?: DispatchJobSchedule
    /** Links assignment back to the originating unassigned job. */
    sourceJobId?: string
    allocations?: JobAllocation[]
    requirements?: JobRequirement[]
    allocationStatus?: AllocationStatus
    warnings?: string[]
    boardScenario?: DispatchBoardScenario
}

export type HourlyLoad = {
    slotKey: string
    value: number
}

export type ScheduleDate = {
    /** Full label, e.g. "Thursday, Jun 5, 2026" */
    label: string
    weekdayShort: string
    dayOfMonth: number
    monthLabel: string
    isToday?: boolean
}

export type DriverFitGroup = 'recommended' | 'available'

export type DriverFitOption = {
    driverId: string
    driverName: string
    group: DriverFitGroup
    reasons: string[]
}

export type AttentionUrgency = 'critical' | 'soon' | 'later'

export type DispatchTimelineStats = {
    jobsStartingToday: number
    jobsWithoutDriver: number
    /** Unassigned jobs starting within the next few hours (critical + soon). */
    needsYouNow: number
    /** Assigned jobs visible on the board. */
    jobsOnBoard: number
}

export type DispatchTimelineData = {
    fieldLabel: string
    scheduleDate: ScheduleDate
    timeSlots: DispatchTimeSlot[]
    unassignedJobs: UnassignedJob[]
    /** Board job identity keyed by persisted job id — sourced once from the board payload. */
    jobIdentities?: Record<string, BoardJobIdentity>
    resources: DispatchResource[]
    /** Rental assets available for allocation. */
    equipment: DispatchResource[]
    assignments: DispatchAssignment[]
    hourlyLoad: HourlyLoad[]
    /** Populated on the projected view shown in the grid. */
    viewRange?: ViewRange
    /** Days covered by the loaded schedule — used for queue labels independent of horizon. */
    scheduleDays?: ScheduleDate[]
    visibleDays?: ScheduleDate[]
    /** Days from today to the first visible day (negative when the week starts on Monday). */
    viewDayOriginOffset?: number
    /** Inclusive API window start (`YYYY-MM-DD`) for live board loads. Job dayOffsets are relative to this date. */
    scheduleWindowStart?: string
    /** Inclusive API window end (`YYYY-MM-DD`) for live board loads. */
    scheduleWindowEnd?: string
    slotsPerDay?: number
    slotColumnMinWidth?: number
}
